import { Action } from '@ngrx/store';
import { List } from 'immutable';

import { BibliographicResource } from '®shared/bibliographic-resource';
import { CreateOrUpdate } from '®shared/container-structure/state/create-or-update';
import { EditableItem, PublishActionStatus } from '®shared/reading-list/models';
import { Importance } from '®shared/tenant';

export enum ContainerEditActionTypes {
  CREATE_NOTE_ITEM = '[List edit] Create note-only item',
  CANCEL_CREATE_NOTE_ITEM = '[List edit] Cancel create note-only item',
  CONFIRM_CREATE_NOTE_ITEM = '[List edit] Confirm create note-only item',

  CREATE_RESOURCE_ITEM = '[List edit] Create item with resource',
  CANCEL_CREATE_RESOURCE_ITEM = '[List edit] Cancel create item with resource',
  CONFIRM_CREATE_RESOURCE_ITEM = '[List edit] Confirm create item with resource',

  CREATE_SECTION = '[List edit] Create section',
  CANCEL_CREATE_SECTION = '[List edit] Cancel create section',
  CONFIRM_CREATE_SECTION = '[List edit] Confirm create section',

  DELETE_ITEM = '[List edit] Delete item',
  CANCEL_DELETE_ITEM = '[List edit] Cancel delete item',
  CONFIRM_DELETE_ITEM = '[List edit] Confirm delete item',

  UPDATE_NOTE_ITEM = '[List edit] Update note item',
  CANCEL_UPDATE_NOTE_ITEM = '[List edit] Cancel update note item',
  CONFIRM_UPDATE_NOTE_ITEM = '[List edit] Confirm update note item',

  UPDATE_RESOURCE_ITEM_IMPORTANCE = '[List edit] Update resource item importance',
  DELETE_RESOURCE_ITEM_IMPORTANCE = '[List edit] Delete resource item importance',

  DELETE_RESOURCE_ITEM_STUDENT_NOTE = '[List edit] Delete resource item note-for-student',
  UPDATE_RESOURCE_ITEM_STUDENT_NOTE = '[List edit] Update resource item note-for-student',
  CANCEL_UPDATE_RESOURCE_ITEM_STUDENT_NOTE = '[List edit] Cancel update resource item note-for-student',
  CONFIRM_UPDATE_RESOURCE_ITEM_STUDENT_NOTE = '[List edit] Confirm update resource item note-for-student',

  DELETE_RESOURCE_ITEM_LIBRARY_NOTE = '[List edit] Delete resource item note-for-library',
  UPDATE_RESOURCE_ITEM_LIBRARY_NOTE = '[List edit] Update resource item note-for-library',
  CANCEL_UPDATE_RESOURCE_ITEM_LIBRARY_NOTE = '[List edit] Cancel update resource item note-for-library',
  CONFIRM_UPDATE_RESOURCE_ITEM_LIBRARY_NOTE = '[List edit] Confirm update resource item note-for-library',

  DELETE_SECTION = '[List edit] Delete section',
  CANCEL_DELETE_SECTION = '[List edit] Cancel delete section',
  CONFIRM_DELETE_SECTION = '[List edit] Confirm delete section',

  UPDATE_SECTION = '[List edit] Update section',
  CANCEL_UPDATE_SECTION = '[List edit] Cancel update section',
  CONFIRM_UPDATE_SECTION = '[List edit] Confirm update section',

  MOVE_ITEM = '[List edit] Move item',
  MOVE_SECTION = '[List edit] Move section',

  CUT_ITEM = '[List edit] Cut item',
  CANCEL_CUT_ITEM = '[List edit] Cancel cut item',
  PASTE_ITEM = '[List edit] Paste item',

  CUT_SECTION = '[List edit] Cut section',
  CANCEL_CUT_SECTION = '[List edit] Cancel cut section',
  PASTE_SECTION = '[List edit] Paste section',

  PUBLISH_LIST = '[List edit] Publish list',
  RESUME_PUBLISH_LIST = '[List edit] Resume publish list',

  PUBLISH_LIST_AND_REQUEST_REVIEW = '[List edit] Publish list and request review',
}

// Shared types in payloads

export enum CutOrCopy {
  COPY = 'copy',
  CUT = 'cut',
}

export enum MoveActionSources {
  UP = 'up',
  DOWN = 'down',
  DROP = 'drop',
  PASTE = 'paste',
}

export enum ResourceSource {
  BOOKMARK = 'bookmark',
  QUICK_ADD = 'quick_add',
  DISCOVERY_SYSTEM = 'discovery_system',
}

export interface EntryIdentifier {
  id: string;
  type: 'items' | 'sections';
}

export interface ContainerIdentifier {
  id: string;
  type: 'lists' | 'sections';
}
export interface ContainerIdentifierWithIndex extends ContainerIdentifier {
  index: number;
}

export interface ContainerEditPayload {
  listId: string;
}

export type ResumePublishListPayload = ContainerEditPayload & {
  listPublishActionId: string;
  listEtag: string;
  status: PublishActionStatus;
};

export interface HasItemId extends ContainerEditPayload {
  id: string;
}

export interface HasSectionId extends ContainerEditPayload {
  id: string;
}

export interface CreateOrUpdateStudentNote extends HasItemId {
  createOrUpdate: CreateOrUpdate;
}

export interface CreateOrUpdateLibraryNote extends HasItemId {
  createOrUpdate: CreateOrUpdate;
}

// Create item with note-for-student only

export interface CreateNoteItem extends HasItemId {
  to: ContainerIdentifierWithIndex;
}

export class CreateNoteItemAction implements Action {
  readonly type = ContainerEditActionTypes.CREATE_NOTE_ITEM;

  constructor(public payload: CreateNoteItem) {}
}

export class CancelCreateNoteItemAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_CREATE_NOTE_ITEM;

  constructor(public payload: CreateNoteItem) {}
}

export interface ConfirmCreateNoteItem extends CreateNoteItem {
  student_note: string;
}

export class ConfirmCreateNoteItemAction implements Action {
  readonly type = ContainerEditActionTypes.CONFIRM_CREATE_NOTE_ITEM;

  constructor(public payload: ConfirmCreateNoteItem) {}
}

// Create item with resource

export interface CreateResourceItem extends HasItemId {
  to: ContainerIdentifierWithIndex;
  returnFocusSelector?: string;
}

export class CreateResourceItemAction implements Action {
  readonly type = ContainerEditActionTypes.CREATE_RESOURCE_ITEM;

  constructor(public payload: CreateResourceItem) {}
}

export class CancelCreateResourceItemAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_CREATE_RESOURCE_ITEM;

  constructor(public payload: ContainerEditPayload) {}
}

export interface ConfirmCreateResourceItem extends HasItemId {
  resource: BibliographicResource;
  to: ContainerIdentifierWithIndex;
  source: ResourceSource;
}

export class ConfirmCreateResourceItemAction implements Action {
  readonly type = ContainerEditActionTypes.CONFIRM_CREATE_RESOURCE_ITEM;

  constructor(public payload: ConfirmCreateResourceItem) {}
}

// Create section

export interface CreateSection extends HasSectionId {
  to: ContainerIdentifierWithIndex;
}

export class CreateSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CREATE_SECTION;

  constructor(public payload: CreateSection) {}
}

export class CancelCreateSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_CREATE_SECTION;

  constructor(public payload: CreateSection) {}
}

export interface ConfirmCreateSection extends CreateSection {
  title: string;
  description?: string;
}

export class ConfirmCreateSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CONFIRM_CREATE_SECTION;

  constructor(public payload: ConfirmCreateSection) {}
}

// Delete item

export interface DeleteItem extends ContainerEditPayload {
  item: EntryIdentifier;
  from: ContainerIdentifierWithIndex;
  hasActiveCourseReserve?: boolean;
  hasReferredCourseReserve?: boolean;
}

export class DeleteItemAction implements Action {
  readonly type = ContainerEditActionTypes.DELETE_ITEM;

  constructor(public payload: DeleteItem) {}
}

export class CancelDeleteItemAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_DELETE_ITEM;

  constructor(public payload: DeleteItem) {}
}

export class ConfirmDeleteItemAction implements Action {
  readonly type = ContainerEditActionTypes.CONFIRM_DELETE_ITEM;

  constructor(public payload: DeleteItem) {}
}

// Delete section

export interface DeleteSection extends ContainerEditPayload {
  section: EntryIdentifier;
  from: ContainerIdentifierWithIndex;
  descendantItems: List<string>;
  descendantSections: List<string>;
  hasActiveCourseReserve?: boolean;
  hasReferredCourseReserve?: boolean;
}

export class DeleteSectionAction implements Action {
  readonly type = ContainerEditActionTypes.DELETE_SECTION;

  constructor(public payload: DeleteSection) {}
}

export class CancelDeleteSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_DELETE_SECTION;

  constructor(public payload: DeleteSection) {}
}

export class ConfirmDeleteSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CONFIRM_DELETE_SECTION;

  constructor(public payload: DeleteSection) {}
}

// Update note-for-student (only) item

export class UpdateNoteItemAction implements Action {
  readonly type = ContainerEditActionTypes.UPDATE_NOTE_ITEM;

  constructor(public payload: HasItemId) {}
}

export class CancelUpdateNoteItemAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_UPDATE_NOTE_ITEM;

  constructor(public payload: HasItemId) {}
}

export interface ConfirmUpdateNoteItem extends HasItemId {
  student_note: string;
}

export class ConfirmUpdateNoteItemAction implements Action {
  readonly type = ContainerEditActionTypes.CONFIRM_UPDATE_NOTE_ITEM;

  constructor(public payload: ConfirmUpdateNoteItem) {}
}

// Update resource item importance

export interface ItemImportance extends HasItemId {
  importance: Importance;
  createOrUpdate: CreateOrUpdate;
}

export class UpdateResourceItemImportanceAction implements Action {
  readonly type = ContainerEditActionTypes.UPDATE_RESOURCE_ITEM_IMPORTANCE;

  constructor(public payload: ItemImportance) {}
}

export class DeleteResourceItemImportanceAction implements Action {
  readonly type = ContainerEditActionTypes.DELETE_RESOURCE_ITEM_IMPORTANCE;

  constructor(public payload: HasItemId) {}
}

// Update resource item note-for-student

export class DeleteResourceItemStudentNoteAction implements Action {
  readonly type = ContainerEditActionTypes.DELETE_RESOURCE_ITEM_STUDENT_NOTE;

  constructor(public payload: HasItemId) {}
}

export class UpdateResourceItemStudentNoteAction implements Action {
  readonly type = ContainerEditActionTypes.UPDATE_RESOURCE_ITEM_STUDENT_NOTE;

  constructor(public payload: CreateOrUpdateStudentNote) {}
}

export class CancelUpdateResourceItemStudentNoteAction implements Action {
  readonly type =
    ContainerEditActionTypes.CANCEL_UPDATE_RESOURCE_ITEM_STUDENT_NOTE;

  constructor(public payload: CreateOrUpdateStudentNote) {}
}

export interface ConfirmUpdateResourceItemStudentNote
  extends CreateOrUpdateStudentNote {
  student_note: string;
}

export class ConfirmUpdateResourceItemStudentNoteAction implements Action {
  readonly type =
    ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_STUDENT_NOTE;

  constructor(public payload: ConfirmUpdateResourceItemStudentNote) {}
}

// Update resource item note-for-student

export class DeleteResourceItemLibraryNoteAction implements Action {
  readonly type = ContainerEditActionTypes.DELETE_RESOURCE_ITEM_LIBRARY_NOTE;

  constructor(public payload: HasItemId) {}
}

export class UpdateResourceItemLibraryNoteAction implements Action {
  readonly type = ContainerEditActionTypes.UPDATE_RESOURCE_ITEM_LIBRARY_NOTE;

  constructor(public payload: CreateOrUpdateLibraryNote) {}
}

export class CancelUpdateResourceItemLibraryNoteAction implements Action {
  readonly type =
    ContainerEditActionTypes.CANCEL_UPDATE_RESOURCE_ITEM_LIBRARY_NOTE;

  constructor(public payload: CreateOrUpdateLibraryNote) {}
}

export interface ConfirmUpdateResourceItemLibraryNote
  extends CreateOrUpdateLibraryNote {
  library_note: string;
}

export class ConfirmUpdateResourceItemLibraryNoteAction implements Action {
  readonly type =
    ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_LIBRARY_NOTE;

  constructor(public payload: ConfirmUpdateResourceItemLibraryNote) {}
}

// Update section

export class UpdateSectionAction implements Action {
  readonly type = ContainerEditActionTypes.UPDATE_SECTION;

  constructor(public payload: HasSectionId) {}
}

export class CancelUpdateSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_UPDATE_SECTION;

  constructor(public payload: HasSectionId) {}
}

export interface ConfirmUpdateSection extends HasSectionId {
  title: string;
  description: string;
}

export class ConfirmUpdateSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CONFIRM_UPDATE_SECTION;

  constructor(public payload: ConfirmUpdateSection) {}
}

// Move item

export interface MoveItem extends ContainerEditPayload {
  item: EditableItem;
  source: MoveActionSources;
  from: ContainerIdentifierWithIndex;
  to: ContainerIdentifierWithIndex;
}

export class MoveItemAction implements Action {
  readonly type = ContainerEditActionTypes.MOVE_ITEM;

  constructor(public payload: MoveItem) {}
}

// Move section

export interface MoveSection extends ContainerEditPayload {
  section: EntryIdentifier;
  source: MoveActionSources;
  from: ContainerIdentifierWithIndex;
  to: ContainerIdentifierWithIndex;
}

export class MoveSectionAction implements Action {
  readonly type = ContainerEditActionTypes.MOVE_SECTION;

  constructor(public payload: MoveSection) {}
}

// Cut/paste item

export interface CutOrCopyItem extends ContainerEditPayload {
  cutOrCopy: CutOrCopy;
  item: EditableItem | (EntryIdentifier & { type: 'items' });
  from: ContainerIdentifierWithIndex;
}

export interface PasteItem extends CutOrCopyItem {
  to: ContainerIdentifierWithIndex;
}

export class CutItemAction implements Action {
  readonly type = ContainerEditActionTypes.CUT_ITEM;

  constructor(public payload: CutOrCopyItem) {}
}

export class CancelCutItemAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_CUT_ITEM;

  constructor(public payload: CutOrCopyItem) {}
}

export class PasteItemAction implements Action {
  readonly type = ContainerEditActionTypes.PASTE_ITEM;

  constructor(public payload: PasteItem) {}
}

// Cut/paste section

export interface CutOrCopySection extends ContainerEditPayload {
  cutOrCopy: CutOrCopy;
  section: EntryIdentifier;
  from: ContainerIdentifierWithIndex;
}

export interface PasteSection extends CutOrCopySection {
  to: ContainerIdentifierWithIndex;
}

export class CutSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CUT_SECTION;

  constructor(public payload: CutOrCopySection) {}
}

export class CancelCutSectionAction implements Action {
  readonly type = ContainerEditActionTypes.CANCEL_CUT_SECTION;

  constructor(public payload: CutOrCopySection) {}
}

export class PasteSectionAction implements Action {
  readonly type = ContainerEditActionTypes.PASTE_SECTION;

  constructor(public payload: PasteSection) {}
}

export class PublishListAction implements Action {
  readonly type = ContainerEditActionTypes.PUBLISH_LIST;

  constructor(public payload: ContainerEditPayload) {}
}

export class PublishListAndRequestReviewAction implements Action {
  readonly type = ContainerEditActionTypes.PUBLISH_LIST_AND_REQUEST_REVIEW;

  constructor(public payload: ContainerEditPayload) {}
}

export class ResumePublishListAction implements Action {
  readonly type = ContainerEditActionTypes.RESUME_PUBLISH_LIST;

  constructor(public payload: ResumePublishListPayload) {}
}

export type ClipboardActions =
  | CutItemAction
  | PasteItemAction
  | CancelCutItemAction
  | CutSectionAction
  | PasteSectionAction
  | CancelCutSectionAction;

export type ContainerEditActions =
  | CreateNoteItemAction
  | CancelCreateNoteItemAction
  | ConfirmCreateNoteItemAction
  | UpdateNoteItemAction
  | CancelUpdateNoteItemAction
  | ConfirmUpdateNoteItemAction
  | CreateResourceItemAction
  | CancelCreateResourceItemAction
  | ConfirmCreateResourceItemAction
  | UpdateResourceItemImportanceAction
  | DeleteResourceItemImportanceAction
  | DeleteResourceItemStudentNoteAction
  | UpdateResourceItemStudentNoteAction
  | CancelUpdateResourceItemStudentNoteAction
  | ConfirmUpdateResourceItemStudentNoteAction
  | DeleteResourceItemLibraryNoteAction
  | UpdateResourceItemLibraryNoteAction
  | CancelUpdateResourceItemLibraryNoteAction
  | ConfirmUpdateResourceItemLibraryNoteAction
  | UpdateSectionAction
  | CancelUpdateSectionAction
  | ConfirmUpdateSectionAction
  | CreateSectionAction
  | CancelCreateSectionAction
  | ConfirmCreateSectionAction
  | DeleteItemAction
  | CancelDeleteItemAction
  | ConfirmDeleteItemAction
  | DeleteSectionAction
  | CancelDeleteSectionAction
  | ConfirmDeleteSectionAction
  | MoveItemAction
  | MoveSectionAction
  | CutItemAction
  | CancelCutItemAction
  | PasteItemAction
  | CutSectionAction
  | CancelCutSectionAction
  | PasteSectionAction
  | PublishListAction
  | ResumePublishListAction;
